import axios from '../../plugins/axios';
import { CACHE_TIME } from '@/js/utils/vars';

export const namespaced = true;

export const state = () => ({
    entities: [],
    entitiesCount: 0,
    entity: {},
    categories: [],
    products: [],
    popular_list: [],
    recomendations: [],
    productsCount: 0,
    product: {},
    home_page_entities: [],
    // Cache timestamps for 5-minute caching
    cacheTimestamps: {
        fetch: 0,
        homePage: 0,
        fetchByTower: {},
        popular: {},
        recomendations: {}
    }
});

export const mutations = {
    CLEAR_ENTITIES(state) {
        state.entities = [];
        state.entitiesCount = 0;
    },
    CLEAR_ENTITIES_MAIN_PAGE(state) {
        state.home_page_entities = [];
    },
    CLEAR_ENTITY(state) {
        state.entity = {};
        state.categories = [];
    },
    CLEAR_CATEGORIES(state) {
        state.categories = [];
    },
    CLEAR_PRODUCTS(state) {
        state.products = [];
        state.productsCount = 0;
    },
    CLEAR_PRODUCT(state) {
        state.product = {};
    },
    SET_ENTITIES(state, payload) {
        state.entities = payload;
    },
    SET_ENTITIES_MAIN_PAGE(state, payload) {
        state.home_page_entities = payload;
    },
    SET_ENTITY(state, payload) {
        state.entity = payload;
    },
    SET_CATEGORIES(state, payload) {
        state.categories = payload;
    },
    SET_PRODUCTS(state, payload) {
        if(payload.page === 1) {
            state.products = [];
        }
        state.products = state.products.concat(payload.products);
        state.productsCount = payload.productsCount;
    },
    SET_POPULAR(state, payload) {
        state.popular_list = payload;
    },
    SET_RECOMENDATIONS(state, payload) {
        state.recomendations = payload;
    },
    SET_PRODUCT(state, payload) {
        state.product = payload;
    },
    UPDATE_CACHE_TIMESTAMP(state, { method, key = null, timestamp }) {
        if (key) {
            state.cacheTimestamps[method][key] = timestamp;
        } else {
            state.cacheTimestamps[method] = timestamp;
        }
    }
};

export const actions = {
    async fetch({ commit, state }, { forced = false }) {
        const now = (new Date()).getTime();
        const cacheValid = !forced && state.entities.length && (now - state.cacheTimestamps.fetch < CACHE_TIME);
        
        if (cacheValid) {
            return state.entities;
        }
        
        const { data } = await axios.get('/front/grocers');
        commit('SET_ENTITIES', data);
        commit('UPDATE_CACHE_TIMESTAMP', { method: 'fetch', timestamp: now });
        return data;
    },
    async homePage({ commit, state }, { forced = false } = {}) {
        const now = (new Date()).getTime();
        const cacheValid = !forced && state.home_page_entities.length && (now - state.cacheTimestamps.homePage < CACHE_TIME);
        
        if (cacheValid) {
            return state.home_page_entities;
        }
        
        const { data } = await axios.get('/front/grocers/main-page');
        commit('SET_ENTITIES_MAIN_PAGE', data);
        commit('UPDATE_CACHE_TIMESTAMP', { method: 'homePage', timestamp: now });
        return data;
    },
    async fetchByTower({ commit, state }, { tower_id, forced = false }) {
        const now = (new Date()).getTime();
        const cacheKey = tower_id.toString();
        const cacheValid = !forced && state.entities.length && 
                          state.cacheTimestamps.fetchByTower[cacheKey] &&
                          (now - state.cacheTimestamps.fetchByTower[cacheKey] < CACHE_TIME);
        
        if (cacheValid) {
            return state.entities;
        }
        
        const { data } = await axios.get(`/front/grocers/sort-for-tower/${tower_id}`);
        commit('SET_ENTITIES', data);
        commit('UPDATE_CACHE_TIMESTAMP', { method: 'fetchByTower', key: cacheKey, timestamp: now });
        return data;
    },
    async get({ commit }, { grocerId }) {
        const { data } = await axios.get(`/front/grocers/${ grocerId }`);
        commit('SET_CATEGORIES', data.categories);
        delete data.categories;
        commit('SET_ENTITY', data);
    },
    async getByCode({ commit }, { grocerCode }) {
        const { data } = await axios.get(`/front/grocers/code/${ grocerCode }`);
        commit('SET_CATEGORIES', data.categories);
        delete data.categories;
        commit('SET_ENTITY', data);
        return data;
    },
    async getProducts({ commit }, { grocerId, categoryId, page }) {
        if(page === 1) {
            commit('SET_PRODUCTS', { products: [], productsCount: 0, page: 1 });
        }
        const { data } = await axios.get(`/front/grocers/${ grocerId }/category/${ categoryId }?page=${ page }`);
        commit('SET_PRODUCTS', { products: data.results, productsCount: data.total, page });
    },
    async getPopular({ commit, state }, { grocerId, forced = false }) {
        const now = (new Date()).getTime();
        const cacheKey = grocerId.toString();
        const cacheValid = !forced && state.popular_list.length && 
                          state.cacheTimestamps.popular[cacheKey] &&
                          (now - state.cacheTimestamps.popular[cacheKey] < CACHE_TIME);
        
        if (cacheValid) {
            return state.popular_list;
        }
        
        const { data } = await axios.get(`/front/grocers/${ grocerId }/popular`);
        commit('SET_POPULAR', data);
        commit('UPDATE_CACHE_TIMESTAMP', { method: 'popular', key: cacheKey, timestamp: now });
        return data;
    },
    async getRecomendations({ commit, state }, { grocerId, forced = false }) {
        const { data } = await axios.get(`/front/grocers/${ grocerId }/recomendations`);
        commit('SET_RECOMENDATIONS', data);
        return data;
    },
    async getProduct({ commit }, { grocerId, productId }) {
        commit('SET_PRODUCT', {});
        const { data } = await axios.get(`/front/grocers/${ grocerId }/product/${ productId }`);
        commit('SET_PRODUCT', data);
    }
};

export const getters = {
    categoriesFirstLevel: state => {
        return state.categories.filter(c => (c.parent_id === null || c.parent_id === 0 || c.id === 'popular'))
    },

    categoriesChildrenMap: state => {
        const res = { 0: [] };

        state.categories.forEach(category => {
            if (!category.parent_id) {
                res[0].push(category);
                return;
            }

            if (res[category.parent_id]) res[category.parent_id].push(category);
            else res[category.parent_id] = [category];
        });

        return res;
    },

    categoriesByIdMap: state => {
        const res = {};

        state.categories.forEach(category => {

            res[category.id ?? 0] = category;

            return res;
        });

        return res;
    },

    addedSubcategoriesCategoriesByIdMap: (state, { categoriesByIdMap, categoriesChildrenMap }) => {
        const res = {
            0: { id: 'root', subcategories: categoriesChildrenMap[0] }
        };

        Object.keys(categoriesByIdMap).forEach(id => {
            const newCategory = { ...categoriesByIdMap[id], subcategories: categoriesChildrenMap[id] ?? [] };

            if (!res[id]) res[id] = newCategory;
        });

        return res;
    }
};
